/**
 * Element helpers, shared by every screen.
 *
 * Everything is createElement + textContent. No innerHTML anywhere in this app
 * (§3.8), so nothing that arrives from an API — or from a document someone
 * uploaded — can become markup.
 */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export const byId = (id: string): HTMLElement | null => document.getElementById(id);

/** The handoff keeps `→` as a character in prose contexts rather than an icon. */
export const ARROW = "→";

export function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric" }).format(
    new Date(Date.UTC(y, m - 1, d)),
  );
}

export function sectionHead(title: string, sub?: string): HTMLElement {
  const head = el("div", "section-head");
  head.append(el("h2", undefined, title));
  if (sub) head.append(el("span", "sub", sub));
  return head;
}

/**
 * The marker every unwired screen carries.
 *
 * The design is drawn against a worked example — a named applicant in Dakar, a
 * named sponsor in Leeds, fourteen specific documents. Rendering that inside a
 * real person's signed-in account, unlabelled, would be showing them a case
 * that is not theirs and inviting them to act on it. The handoff itself calls
 * its data "illustrative but internally consistent… fixture data, not legally
 * verified content".
 *
 * So the layout is faithful and the content is honest about what it is. When a
 * screen gets its real data source, delete the call.
 */
export function previewBanner(what: string): HTMLElement {
  const banner = el("aside", "preview-banner");
  banner.append(el("span", "tag", "Example content"));
  banner.append(
    el(
      "p",
      undefined,
      `${what} This screen shows the design filled with example content so the shape is reviewable. None of it is from your case, and nothing here is saved.`,
    ),
  );
  return banner;
}
